import { useState, useEffect } from "react";
import { useSearch } from "wouter";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const NAVY = "#0E2841";
const TEAL = "#156082";

interface SessionStatus {
  status: "complete" | "expired" | "open" | string;
  customerEmail: string | null;
  program: string | null;
  method: string | null;
  isTest: boolean;
}

export default function Fundable360CheckoutReturn() {
  const search = useSearch();
  const params = new URLSearchParams(search);
  const sessionId = params.get("session_id") ?? "";

  const [status, setStatus] = useState<SessionStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) { setError("No session ID found."); return; }
    fetch(`/api/billing/fundable360/checkout/session-status?session_id=${encodeURIComponent(sessionId)}`)
      .then(r => r.json())
      .then((data: SessionStatus & { error?: string }) => {
        if (data.error) { setError(data.error); return; }
        setStatus(data);
      })
      .catch(() => setError("Unable to verify payment status. Please contact support."));
  }, [sessionId]);

  const isFinanced = status?.method === "financed";

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
      <div className="w-full fixed top-0 left-0" style={{ background: NAVY }}>
        <div className="max-w-5xl mx-auto px-4 py-4">
          <span className="text-white font-serif font-bold text-lg tracking-wide">FUNDABLE360</span>
        </div>
      </div>
      <div className="mt-20" />

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-10 max-w-md w-full text-center">
        {!status && !error && (
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            <p className="text-gray-500">Verifying your payment…</p>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center gap-4">
            <XCircle className="w-12 h-12 text-red-400" />
            <h1 className="text-xl font-bold text-gray-900">Something went wrong</h1>
            <p className="text-sm text-gray-500">{error}</p>
            <a
              href="https://fundable360.wibiz.ai/"
              className="mt-2 inline-block text-sm font-semibold hover:underline"
              style={{ color: TEAL }}
            >
              ← Back to Fundable360
            </a>
          </div>
        )}

        {status?.status === "complete" && (
          <div className="flex flex-col items-center gap-4">
            <CheckCircle2 className="w-14 h-14 text-green-500" />
            <h1 className="text-2xl font-bold text-gray-900">Welcome to Fundable360!</h1>
            <p className="text-gray-600">
              {isFinanced ? "Your first payment is confirmed." : "Your enrollment is confirmed."}
              {status.customerEmail && (
                <>
                  {" "}A confirmation has been sent to{" "}
                  <span className="font-semibold">{status.customerEmail}</span>.
                </>
              )}
            </p>
            {isFinanced && (
              <p className="text-sm text-gray-500">
                Your remaining payments will be charged automatically on the same schedule each month.
              </p>
            )}
            <p className="text-sm text-gray-500">
              Our Concierge team will reach out shortly to get you onboarded.
            </p>
            {status.isTest && (
              <span className="text-xs bg-blue-100 text-blue-700 font-medium px-3 py-1 rounded-full">
                Test payment - no real charge
              </span>
            )}
            <a
              href="https://fundable360.wibiz.ai/"
              className="mt-2 inline-block px-5 py-2.5 rounded-lg text-sm font-semibold text-white"
              style={{ background: TEAL }}
            >
              Go to Fundable360
            </a>
          </div>
        )}

        {status && status.status !== "complete" && (
          <div className="flex flex-col items-center gap-4">
            <XCircle className="w-12 h-12 text-amber-400" />
            <h1 className="text-xl font-bold text-gray-900">Payment not completed</h1>
            <p className="text-sm text-gray-500">
              Your payment was not processed. You have not been charged.
            </p>
            <a
              href="https://fundable360.wibiz.ai/"
              className="mt-2 inline-block text-sm font-semibold hover:underline"
              style={{ color: TEAL }}
            >
              ← Back to Fundable360
            </a>
          </div>
        )}
      </div>

      <p className="mt-6 text-xs text-gray-400">
        Need help? Email us at{" "}
        <a href="mailto:support@bizconnect360.com" className="underline hover:text-gray-600">
          support@bizconnect360.com
        </a>
      </p>
    </div>
  );
}
